import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { StatisticView } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import DateInput from './DateInput';

const Statistics: React.FC = () => {
  const [statisticList, setStatisticList] = useState<StatisticView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [beginDate, setBeginDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    date.setMonth(0);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const COLORS = ['#111111', '#374151', '#4b5563', '#52525b', '#6b7280', '#a1a1aa'];

  // Currency symbol mapping
  const getCurrencySymbol = (currencyCode: string): string => {
    const symbols: { [key: string]: string } = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'UAH': '₴',
      'PLN': 'zł',
      'CZK': 'Kč',
      'RUB': '₽',
      'INR': '₹',
      'BRL': 'R$',
      'KRW': '₩',
      'TRY': '₺',
      'CHF': 'CHF',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'AUD': 'A$',
      'CAD': 'C$',
      'NZD': 'NZ$',
      'HKD': 'HK$',
      'SGD': 'S$',
    };
    return symbols[currencyCode.toUpperCase()] || currencyCode;
  };

  const getData = useCallback(async (beginDateOverride?: string, endDateOverride?: string) => {
    try {
      setLoading(true);
      setStatisticList(null);
      
      const response = await api.get(`/statistics`, {
        params: {
          beginDate: new Date(beginDateOverride || beginDate).toISOString(),
          endDate: new Date(endDateOverride || endDate).toISOString()
        }
      });
      
      setStatisticList(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Initial load with current dates
    getData(beginDate, endDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    getData(beginDate, endDate);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: '0.75rem'
      }}>
        <div className="spinner-border" role="status"></div>
        <p style={{ color: 'var(--text-muted)' }}>Loading statistics...</p>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: '1rem' }}>
        <h1 style={{
          color: 'var(--text-primary)',
          fontSize: 'var(--font-size-3xl)',
          fontWeight: '700',
          marginBottom: '0.35rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          Statistics
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
          View financial statistics and charts
        </p>
      </div>
      
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <h5 className="card-title">Date Range</h5>
          </div>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end statistics-filters">
            <div className="col-md-2">
              <label className="form-label">Begin Date</label>
              <DateInput
                className="form-control"
                value={beginDate}
                maxDate={endDate}
                onChange={(value) => {
                  setBeginDate(value);
                }}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">End Date</label>
              <DateInput
                className="form-control"
                value={endDate}
                minDate={beginDate}
                onChange={(value) => {
                  setEndDate(value);
                }}
              />
            </div>
            
            <div className="col-md-auto">
              <button 
                className="btn btn-primary d-flex align-items-center justify-content-center"
                onClick={handleSearch}
                disabled={statisticList === null}
                style={{ minWidth: '100px', height: '36px', marginBottom: '0' }}
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {statisticList ? (
        <div className="row g-4">
          {statisticList.map((item, index) => (
            <div key={index} className="col-12 col-md-6">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title">{item.data.title}</h5>
                </div>
                <div className="card-body">
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '1rem',
                    alignItems: 'start'
                  }}>
                    <div>
                      <div>
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th
                                className="text-start"
                                style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}
                              >
                                Currency
                              </th>
                              <th
                                className="text-start"
                                style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}
                              >
                                Paid
                              </th>
                              <th
                                className="text-start"
                                style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}
                              >
                                Unpaid
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.data.sums?.map((sum, sumIndex) => (
                              <tr key={sumIndex}>
                                <td style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                                  {sum.currency}
                                </td>
                                <td style={{ color: 'var(--success)' }}>
                                  {getCurrencySymbol(sum.currency)}{sum.sumPaid.toLocaleString()}
                                </td>
                                <td style={{ color: 'var(--warning)' }}>
                                  {getCurrencySymbol(sum.currency)}{sum.sumUnpaid.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    <div style={{ height: '320px', minWidth: '0', padding: '6px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 12, right: 12, bottom: 56, left: 12 }}>
                          <Pie
                            data={item.chatItems}
                            cx="50%"
                            cy="45%"
                            labelLine={false}
                            label={false}
                            outerRadius={84}
                            fill="#4b5563"
                            dataKey="value"
                            nameKey="text"
                          >
                            {item.chatItems.map((_, pieIndex) => (
                              <Cell
                                key={`cell-${pieIndex}`}
                                fill={COLORS[pieIndex % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) => [value.toLocaleString(), 'Amount']}
                            labelStyle={{ color: 'var(--text-primary)' }}
                            contentStyle={{
                              background: 'var(--surface)',
                              border: '1px solid var(--glass-border)',
                              borderRadius: 'var(--radius-md)'
                            }}
                          />
                          <Legend
                            formatter={(value: string, entry: any) => {
                              const total = item.chatItems.reduce((sum, item) => sum + item.value, 0);
                              const percent = ((entry.payload.value / total) * 100).toFixed(2);
                              return `${value} (${percent}%)`;
                            }}
                            wrapperStyle={{
                              color: 'var(--text-secondary)',
                              fontSize: '12px',
                              maxWidth: '100%',
                              overflowWrap: 'break-word',
                              paddingTop: '20px'
                            }}
                            iconSize={10}
                            layout="horizontal"
                            align="center"
                            verticalAlign="bottom"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <div className="spinner-border" role="status"></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading statistics...</p>
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '1.5rem',
          color: 'var(--text-muted)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
            <i className="fa-solid fa-chart-pie" aria-hidden="true"></i>
          </div>
          <h5>No data available</h5>
          <p>No statistics data available for the selected date range.</p>
        </div>
      )}
    </div>
  );
};

export default Statistics;
